import { useEffect } from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import { HiCheckCircle } from "react-icons/hi2";

import FormControl, { FormControlProps } from "@/components/molecules/form/FormControl";

type OptionBase = {
  id: string;
  title?: string;
  description?: string;
};

export type Option<T> = OptionBase & T;

type FormRadioProps<T> = {
  name: string;
  options: Option<T>[];
  rules?: RegisterOptions;
  renderOption?: (option: Option<T>, checked: boolean) => React.ReactNode;
  matchingProperty?: keyof T;
  onChange?: (value: Option<T>) => void;
} & Omit<FormControlProps, "id" | "children">;

const FormRadio = <T,>({
  name,
  options,
  rules,
  renderOption,
  matchingProperty = "id" as keyof T,
  onChange,
  ...rest
}: FormRadioProps<T>) => {
  const { control, getValues, setValue } = useFormContext();

  useEffect(() => {
    if (options.length === 0) return; // nothing to select!
    const currentValue = getValues(name);

    // Check if the current value is in the options
    const found = options.find((option) => option[matchingProperty] === currentValue);
    if (found) {
      setValue(name, found as unknown, {
        shouldValidate: false,
        shouldDirty: false,
      });
      return;
    }

    if (currentValue?.id === undefined || currentValue?.id === null || currentValue?.id === "") {
      setValue(name, options[0] as unknown, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [getValues, setValue, name, options]);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange: rhfOnChange, onBlur, value, ref }, fieldState: { error } }) => (
        <FormControl id={name} error={error?.message} {...rest}>
          <div className={"flex flex-col gap-3"}>
            {options.map((option) => {
              const checked = (value as T | undefined)?.id === option.id;

              const content = renderOption ? (
                renderOption(option, checked)
              ) : (
                <span className="flex flex-1">
                  <span className="flex flex-col">
                    {option.title && <span className="block text-sm font-medium text-gray-900">{option.title}</span>}
                    {option.description && (
                      <span className="mt-1 flex items-center text-sm text-gray-500">{option.description}</span>
                    )}
                  </span>
                </span>
              );

              return (
                <label
                  key={option.id}
                  className="group relative flex rounded-lg border border-gray-300 bg-white p-4 transition-all hover:border-gray-400 has-[:checked]:bg-brand-primary-hover has-[:checked]:outline has-[:focus-visible]:outline has-[:checked]:outline-2 has-[:focus-visible]:outline-[3px] has-[:checked]:-outline-offset-2 has-[:focus-visible]:-outline-offset-1 has-[:checked]:outline-fg-brand-primary"
                >
                  <input
                    ref={ref}
                    type="radio"
                    name={name}
                    checked={checked}
                    onChange={() => {
                      onChange?.(option);
                      setValue(name, option);
                    }}
                    onBlur={onBlur}
                    className="absolute inset-0 appearance-none focus:outline-none"
                  />

                  {content}

                  <HiCheckCircle
                    aria-hidden="true"
                    className="invisible size-5 text-brand-primary group-has-[:checked]:visible"
                  />
                </label>
              );
            })}
          </div>
        </FormControl>
      )}
    />
  );
};

export default FormRadio;
